/** جای هر صندلی روی صفحه. من همیشه پایینم، بقیه به ترتیبِ دور. */
export const spots = ["me", "right", "top", "left"] as const;
export type Spot = (typeof spots)[number];

/**
 * صندلیِ سرور → جای روی صفحه.
 *
 * سرور شماره‌ی صندلیِ مطلق می‌دهد و هر بازیکن خودش را جای دیگری می‌بیند، پس
 * چرخش این‌جا لازم است — همان کاری که HokmOnlineMap در نسخه‌ی موبایل می‌کند.
 */
export function spotOf(seat: number, me: number, count: number): Spot {
  if (count <= 0) return "top";
  const rel = ((seat - me) % count + count) % count;
  return spots[rel] ?? "top";
}

/** جهتِ پرتابِ کارت روی میز، از مرکز به سمتِ صاحبش. */
export const toss: Record<Spot, { x: number; y: number }> = {
  me: { x: 0, y: 52 },
  right: { x: -62, y: 0 },
  top: { x: 0, y: -52 },
  left: { x: 62, y: 0 },
};

/**
 * جایی که کارت از آن‌جا پرواز می‌کند تا وسطِ میز.
 *
 * `toss` بالا فاصله‌ی *نشستنِ* کارت از مرکز است (کوچک، تا چهار کارت جا شوند).
 * پرواز باید از خودِ صندلی شروع شود که خیلی دورتر است، وگرنه کارت انگار از
 * وسطِ میز جوانه می‌زند. ضریب از `SeatRadiusFactor` در TableScene می‌آید.
 */
export const from: Record<Spot, { x: number; y: number }> = {
  me: { x: 0, y: 300 },
  right: { x: -190, y: 30 },
  top: { x: 0, y: -240 },
  left: { x: 190, y: 30 },
};

/**
 * جای دسته‌ی خال‌های بردِ هر صندلی.
 *
 * `me` را اول پایین گذاشته بودم و درست زیرِ بادبزنِ دست می‌افتاد — بود ولی
 * دیده نمی‌شد. حالا بالای دست و کنارِ میز می‌نشیند، جایی که نه روی کارتِ
 * وسط بیفتد نه زیرِ دست.
 */
export const pile: Record<Spot, { x: number; y: number }> = {
  me: { x: 142, y: 128 },
  right: { x: -142, y: -48 },
  top: { x: 128, y: -232 },
  left: { x: 142, y: -48 },
};

/**
 * نشانِ «اشتراکِ ماهانه» کنارِ نام.
 *
 * به نام چسبانده می‌شود و آیکونِ جدا نیست: نامِ حریف در سه میز و ده‌ها جا
 * کشیده می‌شود، و آیکونِ تازه یعنی ده‌ها تغییرِ چیدمان و ده جایی که یادش
 * می‌رود. آینه‌ی Services/SubscriberMark.cs در نسخه‌ی موبایل.
 */
export const SUBSCRIBER_MARK = "\u2726";

/**
 * نامِ ربات بی واژه‌ی «ربات» — نشانش آیکونِ ربات است ([[BotIcon]]). کاربر، ۲۳ اکتبر ۲۰۲۶: «برای
 * ربات از تکستِ ربات استفاده نکن، به جای همه از آیکونِ ربات».
 *
 * سرور نام را هنوز «ربات کاوه» می‌فرستد: اپِ نصب‌شده (APK ≤ 0.0.4) کنارِ نام آیکونی ندارد و بی آن
 * واژه ربات را آدم نشان می‌داد. پس واژه فقط همین‌جا و فقط برای صندلیِ ربات برداشته می‌شود —
 * آدمی که نامش را «ربات علی» گذاشته دست نمی‌خورد.
 */
export function botBare(name: string, bot = true): string {
  if (!bot) return name;
  return name.replace(/^ربات[\s\u200c]+/, "") || name;
}

/** نامِ صندلی در متنِ ساده (پیام، خطِ وضعیت): SVG در رشته نمی‌نشیند، پس ربات با شکلکِ ربات می‌آید. */
export function botText(name: string, bot: boolean | undefined): string {
  return bot ? `🤖 ${botBare(name)}` : name;
}

export function seatName(s: { name?: string; bot?: boolean; subscriber?: boolean }): string {
  const base = s.name || "بازیکن";
  return s.subscriber && !s.bot ? base + " " + SUBSCRIBER_MARK : base;
}
